import Component from '../core/Component'
import PlanCanvas from './PlanCanvas'
import PlanBubble from './PlanBubble'
import Autocomplete from './Autocomplete'
import $ from 'jquery'
import { toggleClass } from '../utils/dom'
import { standardize } from '../utils/String'
import enquire from 'enquire.js'
import throttle from 'lodash/throttle'
import EventBus from '../core/EventBus'
import { logVisit } from './VisitLog'

export const STATES = {
    ACTIVE: 'is-active',
    FILTER_ACTIVE: 'is-filter-active',
    FILTERED: 'is-filtered',
    OPEN: 'is-open',
    PRESSED: 'is-pressed',
    HIGHLIGHTED: 'is-highlighted',
}

export default class Plan extends Component {
    constructor(element) {
        super(element)

        this.ref = {
            zoomIn: null,
            zoomOut: null,
            toolbar: null,
            floorControls: [],
            planCanvas: null,
            planSearch: null,
            bubble: null,
            categoryList: null,
            filterActions: [],
            filterReset: null,
            filterCategoryName: null,
            filterCategory: null,
        }

        this.stores = {}
        this.filterConfig = {}

        this.canZoomScroll = false
        this.zoomScrollEnabled = true

        this.toolbarOpen = false

        this.handleWheel = throttle(::this.handleWheel, 100)

        this.enquireHandler = {
            match: () => {
                this.canZoomScroll = true
            },
            unmatch: () => {
                this.canZoomScroll = false
            },
        }

        this.dragStart = {
            x: 0,
            y: 0,
        }

        this.store = null

        this.enquireQuery = 'screen and (min-height: 60em)'
    }

    prepare() {
        this.ref.zoomIn.addEventListener('click', ::this.handleZoomInClick)
        this.ref.zoomOut.addEventListener('click', ::this.handleZoomOutClick)
        this.ref.filterReset.addEventListener('click', ::this.handleCategoryFilterReset)
        this.ref.filterActions
            .filter((element) => !element.dataset.bindTo)
            .forEach((element) => element.addEventListener('click', this.handleFilterActionClick))
        this.ref.floorControls.forEach((element) =>
            element.addEventListener('click', ::this.handleFloorClick),
        )

        this.floors = this.ref.floorControls.reduce((acc, element) => {
            const id = element.dataset.floor
            const name = element.textContent.trim()
            acc[id] = name
            return acc
        }, {})

        this.canvas = new PlanCanvas(this.ref.planCanvas)
        this.canvas.on({
            'store:click': ::this.handleStoreClick,
            'store:mouseenter': ::this.handleStoreMouseEnter,
            'store:mouseleave': ::this.handleStoreMouseLeave,
            'floor:change': ::this.handleFloorChange,
            'canvas:drag:change': ::this.handleCanvasDragChange,
            'canvas:drag:start': ::this.handleCanvasDragStart,
            'canvas:drag:end': ::this.handleCanvasDragEnd,
            'canvas:zoom:change': ::this.handleCanvasZoomChange,
            'canvas:transition:end': ::this.handleCanvasTransitionEnd,
            'filter:change': this.handleCanvasFilterChange,
            'filter:reset': this.handleCanvasFilterReset,
        })
        this.canvas.prepare()

        this.stores = this.canvas.getStores().map((store) => ({
            ...store,
            floor: store.floor in this.floors ? this.floors[store.floor] : '',
            search: standardize(store.title),
            searchKeywords: standardize(store.keywords),
            searchDescription: standardize(store.description),
        }))

        this.bubble = new PlanBubble(this.ref.bubble)
        this.bubble.prepare()

        this.search = new Autocomplete(this.ref.planSearch, {
            data: this.stores,
            keys: ['search', 'searchKeywords', 'searchDescription'],
        })
        this.search.on({
            select: ::this.handleAutocompleteSelect,
        })
        this.search.prepare()

        this.categories = Component.getFromElement(this.ref.categoryList)
        this.categories.on('click', this.handleCategoryClick)

        this.element.addEventListener('wheel', this.handleWheel)

        enquire.register(this.enquireQuery, this.enquireHandler)

        const storeId = window.location.hash.replace('#store-', '')
        if (storeId) {
            this.canvas.showStore(storeId)
        }
    }

    destroy() {
        this.canvas.destroy()
        this.canvas = null

        this.bubble.destroy()
        this.bubble = null

        this.search.destroy()
        this.search = null

        enquire.unregister(this.enquireQuery, this.enquireHandler)
    }

    handleWheel(event) {
        if (!this.canZoomScroll) {
            return
        }

        if (!this.zoomScrollEnabled) {
            return
        }

        event.preventDefault()

        if (event.deltaY < 0) {
            this.canvas.zoomIn()
        } else {
            this.canvas.zoomOut()
        }
    }

    handleZoomInClick(event) {
        event.preventDefault()
        this.canvas.zoomIn()
    }

    handleZoomOutClick(event) {
        event.preventDefault()
        this.canvas.zoomOut()
    }

    handleFloorClick(event) {
        const { floor, building } = event.currentTarget.dataset

        if (building) {
            this.canvas.setBuilding(building, false)
        }
        this.canvas.setFloor(floor, !!building)
    }

    handleStoreClick(store) {
        $.ajax({
            url: store.url,
        }).then((response) => {
            logVisit({ url: store.url, ref: 'map' })
            const sidebar = Component.getFromElement('map-sidebar')
            if (sidebar) {
                sidebar.on({
                    open: ::this.handleStoreOpen,
                    close: ::this.handleStoreClose,
                })
                sidebar.setContent(response).open()
            }
        })
    }

    handleSearchClick(event) {
        event.preventDefault()

        if (this.toolbarOpen) {
            this.ref.toolbar.classList.remove(STATES.OPEN)
        } else {
            this.ref.toolbar.classList.add(STATES.OPEN)
        }

        this.toolbarOpen = !this.toolbarOpen
    }

    handleFilterActionClick = (event) => {
        event.preventDefault()

        const config = JSON.parse(event.currentTarget.dataset.config)

        Object.keys(config).forEach((key) => {
            if (key in this.filterConfig) {
                delete this.filterConfig[key]
            } else {
                this.filterConfig[key] = config[key]
            }
        })

        this.filter()
    }

    handleStoreMouseEnter({ element, title, desc, id, color, polygon, openingSoon }) {
        if (title) {
            const box = element.getBoundingClientRect()
            const container = this.element.getBoundingClientRect()

            this.store = element

            let anchorOffset = parseFloat(element.dataset.anchorOffset)

            if (isNaN(anchorOffset)) {
                if (polygon) {
                    anchorOffset = this.calculateBubbleAnchorOffset({
                        polygon,
                    })
                } else {
                    anchorOffset = 0
                }

                element.dataset.anchorOffset = anchorOffset
            }

            this.bubble
                .set({
                    title,
                    desc,
                    color,
                    openingSoon,
                })
                .show()

            if (!this.bubble.isPaused) {
                this.bubble.move({
                    x: box.left - container.left + anchorOffset * box.width,
                    y: box.top + box.height - container.top,
                })
            }
        }
    }

    handleStoreMouseLeave({ title, desc, id }) {
        this.store = null
        if (this.bubble) {
            this.bubble.hide()
        }
    }

    handleStoreOpen() {
        this.zoomScrollEnabled = false
    }

    handleStoreClose() {
        this.zoomScrollEnabled = true
    }

    handleFloorChange(floorId) {
        this.ref.floorControls.forEach((element) =>
            toggleClass(element, STATES.ACTIVE, element.dataset.floor === `${floorId}`),
        )

        if (this.bubble) {
            this.bubble.hide()
        }

        EventBus.emit('plan:floor:change', {
            id: floorId,
            label: this.floors[floorId],
        })
    }

    handleAutocompleteSelect(data) {
        this.canvas.filter()
        if ('id' in data) {
            if (this.bubble) {
                this.bubble.pause()
            }
            this.canvas.showStore(data.id)
        }
    }

    handleCanvasDragStart() {
        if (this.bubble) {
            this.bubble.disableAnimation()
        }
    }

    handleCanvasDragEnd() {
        if (this.bubble) {
            this.bubble.enableAnimation()
        }
    }

    handleCanvasDragChange() {
        this.updateBubblePosition()
    }

    handleCanvasZoomChange() {
        this.updateBubblePosition()
    }

    handleCanvasTransitionEnd() {
        this.bubble.resume()
        this.updateBubblePosition()
    }

    handleCategoryClick = ({ id, name }) => {
        if (id === 'all') {
            delete this.filterConfig.categoryId
            this.ref.filterCategoryName.textContent = ""
            this.ref.filterCategory.classList.add('is-hidden')
        } else {
            this.filterConfig.categoryId = id
            this.ref.filterCategoryName.textContent = name
            this.ref.filterCategory.classList.remove('is-hidden')
        }

        this.filter()
    }

    handleCanvasFilterChange = (data) => {
        const { config } = data
        const activeKeys = Object.keys(config)

        this.ref.filterActions.forEach((element) => {
            const params = element.dataset.bindTo ?? element.dataset.config
            const isActive = activeKeys.some((key) => params.indexOf(key) > -1)
            toggleClass(element, STATES.FILTER_ACTIVE, isActive)
        })

        EventBus.emit('plan:filter:change', data)
    }

    handleCanvasFilterReset = () => {
        EventBus.emit('plan:filter:reset')
        this.ref.filterActions.forEach((element) => element.classList.remove(STATES.FILTER_ACTIVE))
    }

    handleCategoryFilterReset() {
        this.filterConfig = {}
        this.ref.filterCategoryName.textContent = ""
        this.ref.filterCategory.classList.add('is-hidden')

        this.filter()
    }

    updateBubblePosition() {
        if (!this.store || !this.bubble || !this.bubble.isActive) {
            return
        }

        const element = this.store

        const box = element.getBoundingClientRect()
        const container = this.element.getBoundingClientRect()

        let anchorOffset = parseFloat(element.dataset.anchorOffset)

        if (isNaN(anchorOffset)) {
            const polygon = element.querySelector('[data-ref="polygon"]')
            anchorOffset = this.calculateBubbleAnchorOffset({
                polygon,
            })

            element.dataset.anchorOffset = anchorOffset
        }

        this.bubble.move({
            x: box.left - container.left + anchorOffset * box.width,
            y: box.top + box.height - container.top,
        })
    }

    calculateBubbleAnchorOffset({ polygon }) {
        let x = 0

        const length = polygon.getTotalLength()
        const sampleSize = Math.floor(Math.max(100, length / 4))
        const box = polygon.getBBox()

        const thresholdY = box.y + box.height - 2

        const anchor = Array(sampleSize)
            .fill(0)
            .map((value, index) => polygon.getPointAtLength((index * length) / sampleSize))
            .filter((point) => point.y > thresholdY)
            .reduce(
                (acc, point) => {
                    if (point.x < acc.x) {
                        acc.x = point.x
                        acc.y = point.y
                    }

                    return acc
                },
                {
                    x: Infinity,
                    y: Infinity,
                },
            )

        if (anchor.x === Infinity) {
            return 0
        }

        return (anchor.x - box.x) / box.width
    }

    getCurrentFloor() {
        return {
            id: this.canvas.currentFloor,
            label: this.floors[this.canvas.currentFloor],
        }
    }

    filter() {
        this.canvas.filter(this.filterConfig)
    }
}
